/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * The component for the page Dashboard
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Misha Kokoulin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.0.1
 */

import { FC, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';

import Title from '../../components/common/Title/Title';
import { pushGTMAnalytic } from '../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMPageDashboardAccountType } from '../../modules/analytics/GoogleTagManager/GTMOperations/GTMPageViewOperations';
import { IGTMPushAnalyticPayload } from '../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import PageLocale from '../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../modules/internationalization/constants';
import { ECommonTranslationPages } from '../../modules/internationalization/types/common/pages';
import { IRootReducer } from '../../reducers/rootReducer';
import {
    makeSelectIsDashboardAvailable,
    makeSelectIsSingleAccount,
    makeSelectUserIsRedirected,
} from '../PageAuth/selectors/authSelectors';
import { loadWidgetMeta } from './components/Widgets/widgetActions';
import WidgetContainer from './components/Widgets/WidgetContainer';
import { makeSelectorWidgetMetaList } from './components/Widgets/widgetSelectors';
import { TWidgetMetaList } from './components/Widgets/widgetTypes';

export const PageDashboard: FC<IDispatchProps & IConnectedProps> =
    ({ isDashboardEnabled,
        logBusinessEvent,
        pushGtmAnalytic,
        loadWidgetMeta,
        isSingleAccount,
        widgetMeta,
        isRedirected,
    }) => {

    useEffect(() => {
        if (isDashboardEnabled) {
            loadWidgetMeta();
            pushGtmAnalytic({
                operation: GTMOperations.pageView.onPageDashboardView(isSingleAccount ?
                    EGTMPageDashboardAccountType.single : EGTMPageDashboardAccountType.many),
                withAccountType: true,
                withSusrId: true,
                withUserRole: true,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDashboardEnabled]);

    useEffect(() => {
        logBusinessEvent({
            eventName: ELogBusinessEventName.B2B_DASHBOARD,
            type: ELogBusinessEventType.common,
        })
        if (isRedirected) {
            logBusinessEvent({
                eventName: ELogBusinessEventName.B2B_FULL_VERSION,
                type: ELogBusinessEventType.common,
            })
        }
    }, [isRedirected, logBusinessEvent])

    return (
        <PageLocale fileNames={[
            TranslationFileNamesEnum.common,
            TranslationFileNamesEnum.pageDashboard,
            TranslationFileNamesEnum.pageAccountInfo,
        ]}>
            <Title pageName={ECommonTranslationPages.dashboard}/>
            {
                isDashboardEnabled &&
                <WidgetContainer widgetMeta={widgetMeta}/>
            }
        </PageLocale>
    );
};

export interface IConnectedProps {
    isDashboardEnabled: boolean;
    isSingleAccount: boolean;
    widgetMeta: TWidgetMetaList;
    isRedirected: boolean;
}

export interface IDispatchProps {
    loadWidgetMeta: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isDashboardEnabled: makeSelectIsDashboardAvailable(),
    isSingleAccount: makeSelectIsSingleAccount(),
    widgetMeta: makeSelectorWidgetMetaList(),
    isRedirected: makeSelectUserIsRedirected(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadWidgetMeta: () => dispatch(loadWidgetMeta()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PageDashboard);
